import React from "react";

// Phase 3D.6-C - Presentational card for the CONSOLIDATED pilot scenario summary
// produced by the canonical engine buildPilotScenarioSummary(...) (3D.6-B, see
// savoiePilotSummary.js). It shows the full demonstration chain in four visual
// steps: (1) detected opportunity, (2) demonstration recommendation,
// (3) simulated campaign, (4) simulated NON-predictive outcomes.
//
// PURE PRESENTATION ONLY:
//   * Renders EXCLUSIVELY the fields already present in summaryResult.
//   * NO analytics engine import/call, NO recompute, NO dataset re-read,
//     NO API/network, NO env read, NO storage, NO mutation, NO fallback.
//   * NO hardcoded market / period / evidence / budget / metric.
//   * Fail-closed: renders NOTHING (null) unless the consolidated contract is
//     entirely valid (ready, synthetic, simulated, non-observed, non-predictive,
//     activation/publish disabled, external id null, all blocks present).
//   * NO button / link / form / editable field / export / share / activation /
//     connect control, NO ad-network brand, NO predictive chart, NO confidence
//     gauge, NO probability, NO expected ROI, NO result promise.
//   * Intl.NumberFormat is used for PRESENTATION only, never for calculation.

import ProvenanceBadges from "@/components/provenance/ProvenanceBadges";

// --- Visual-only formatters (fr-FR). Never used for any calculation. -------
const nf = new Intl.NumberFormat("fr-FR");
const isNum = (v) => typeof v === "number" && Number.isFinite(v);
const NA = "Non disponible";
const fmtInt = (v) => (isNum(v) ? nf.format(Math.round(v)) : NA);
const fmtMoney = (v, c) => (isNum(v) ? `${nf.format(Math.round(v))} ${c || ""}`.trim() : NA);
const fmtPct1 = (v) =>
  isNum(v) ? `${(Math.round(v * 10) / 10).toFixed(1).replace(".", ",")} %` : NA;
const fmtPctSigned = (v) =>
  isNum(v)
    ? `${v >= 0 ? "+" : ""}${(Math.round(v * 10) / 10).toFixed(1).replace(".", ",")} %`
    : NA;

// "2024-07" -> "juillet 2024" (local presentation helper; imports no engine).
const FR_MONTHS = [
  "janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre",
];
function frPeriod(iso) {
  if (typeof iso !== "string") return NA;
  const m = /^(\d{4})-(\d{2})$/.exec(iso);
  if (!m) return NA;
  const year = Number(m[1]);
  const month = Number(m[2]);
  if (month < 1 || month > 12) return NA;
  return `${FR_MONTHS[month - 1]} ${year}`;
}

// Readable label for the closed action-type enum (presentation only; the raw
// value is still displayed alongside so nothing is hidden).
const ACTION_LABELS = { audience_focus: "Concentration de l'attention marketing" };

const isObj = (v) => v !== null && typeof v === "object" && !Array.isArray(v);

function metaConform(meta) {
  return (
    isObj(meta) &&
    meta.is_demo === true &&
    meta.provenance === "synthetic_demo" &&
    meta.provenance_label === "DONNÉES DE DÉMONSTRATION" &&
    meta.affiliation === "none" &&
    meta.simulation === true &&
    meta.simulation_label === "SIMULATION"
  );
}

function transparencyConform(t) {
  return (
    isObj(t) &&
    typeof t.historical_data === "string" &&
    typeof t.campaign === "string" &&
    typeof t.outcomes === "string" &&
    t.outcomes_observed === false &&
    t.outcomes_predictive === false
  );
}

function sourceIdsConform(s) {
  return (
    isObj(s) &&
    typeof s.opportunity_id === "string" &&
    typeof s.recommendation_id === "string" &&
    typeof s.campaign_id === "string" &&
    typeof s.outcomes_id === "string"
  );
}

// Small inline "Hypothèse" tag (not colour-only: carries explicit text).
function AssumptionTag() {
  return (
    <span
      className="inline-flex items-center rounded border border-violet-300 bg-violet-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700"
      data-testid="pilot-summary-assumption-tag"
    >
      Hypothèse
    </span>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-t border-slate-100 py-1.5 first:border-t-0">
      <dt className="text-sm text-slate-600">{label}</dt>
      <dd className="text-sm font-semibold text-slate-900" data-testid={testid}>{value}</dd>
    </div>
  );
}

const OUTCOMES_NOTE = "Valeurs simulées, non mesurées et non prédictives";
const SUBTITLE = "Chaîne complète de démonstration, de l'opportunité aux résultats simulés";

export default function PilotScenarioSummaryCard({ summaryResult }) {
  const s = summaryResult;

  // --- Fail-closed display gate (no partial rendering). -------------------
  if (!isObj(s)) return null;
  if (s.status !== "ready") return null;
  if (s.summary_type !== "synthetic_pilot_scenario") return null;
  if (s.provenance !== "synthetic_demo") return null;
  if (s.simulation !== true) return null;
  if (s.contains_simulation !== true) return null;
  if (s.observed !== false) return null;
  if (s.predictive !== false) return null;
  if (s.not_a_forecast !== true) return null;
  if (s.activation_status !== "disabled") return null;
  if (s.publish_status !== "disabled") return null;
  if (s.external_campaign_id !== null) return null;
  if (!sourceIdsConform(s.source_ids)) return null;
  if (!isObj(s.analytical_evidence)) return null;
  if (!isObj(s.growth_opportunity)) return null;
  if (!isObj(s.marketing_recommendation)) return null;
  if (!isObj(s.campaign_assumptions)) return null;
  if (!isObj(s.simulated_outcomes)) return null;
  if (!transparencyConform(s.transparency)) return null;
  if (!metaConform(s.meta)) return null;
  if (!isObj(s.disclaimers) || typeof s.disclaimers.summary !== "string" || s.disclaimers.summary.length === 0) {
    return null;
  }

  const opp = s.growth_opportunity;
  const ev = s.analytical_evidence;
  const rec = s.marketing_recommendation;
  const cmp = s.campaign_assumptions;
  const out = s.simulated_outcomes;
  const t = s.transparency;

  const recAudience = isObj(rec.audience) ? rec.audience : {};
  const cmpAudience = isObj(cmp.audience) ? cmp.audience : {};
  const cmpPeriod = isObj(cmp.period) ? cmp.period : {};
  const cmpBudget = isObj(cmp.budget) ? cmp.budget : {};
  const cmpChannels = Array.isArray(cmp.channels) ? cmp.channels : [];
  const cmpObjectives = Array.isArray(cmp.objectives) ? cmp.objectives : [];
  const cmpAssumptions = Array.isArray(cmp.assumptions) ? cmp.assumptions : [];
  const disp = isObj(out.scenario_outcomes_display) ? out.scenario_outcomes_display : {};

  return (
    <section
      className="rounded-2xl border border-slate-300 bg-slate-50 p-5 shadow-sm"
      aria-labelledby="pilot-scenario-summary-title"
      data-testid="pilot-scenario-summary-card"
    >
      {/* Header: title, subtitle, provenance/simulation badges, transparency labels. */}
      <div className="flex flex-wrap items-center gap-2">
        <h2
          id="pilot-scenario-summary-title"
          className="font-heading text-lg font-bold tracking-tight text-slate-900 sm:text-xl"
          data-testid="pilot-summary-title"
        >
          Synthèse du scénario pilote
        </h2>
        <ProvenanceBadges meta={s.meta} />
      </div>
      <p className="mt-1 text-sm text-slate-600" data-testid="pilot-summary-subtitle">
        {SUBTITLE}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <span
          className="inline-flex items-center rounded-full border border-slate-300 bg-white px-2.5 py-0.5 text-xs font-semibold text-slate-700"
          data-testid="pilot-summary-non-observed"
        >
          Résultats non observés
        </span>
        <span
          className="inline-flex items-center rounded-full border border-slate-300 bg-white px-2.5 py-0.5 text-xs font-semibold text-slate-700"
          data-testid="pilot-summary-non-predictive"
        >
          Scénario non prédictif
        </span>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* Step 1 - Detected opportunity. */}
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="pilot-summary-opportunity">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            1. Opportunité détectée
          </h3>
          <dl>
            <Row label="Marché sélectionné" value={opp.market_name} testid="pilot-summary-opp-market" />
            <Row label="Code marché" value={opp.market_code} testid="pilot-summary-opp-market-code" />
            <Row label="Période d'évidence" value={frPeriod(ev.evidence_period)} testid="pilot-summary-opp-period" />
            <Row label="Période de comparaison" value={frPeriod(ev.comparison_period)} testid="pilot-summary-opp-comparison" />
            <Row label="Arrivées visiteurs" value={fmtInt(ev.visitor_arrivals)} testid="pilot-summary-opp-arrivals" />
            <Row label="Arrivées année précédente" value={fmtInt(ev.previous_year_visitor_arrivals)} testid="pilot-summary-opp-prev-arrivals" />
            <Row label="Croissance des arrivées" value={fmtPctSigned(ev.growth_percent)} testid="pilot-summary-opp-growth" />
            <Row label="Part de marché" value={fmtPct1(ev.market_share_percent)} testid="pilot-summary-opp-share" />
          </dl>
        </div>

        {/* Step 2 - Demonstration recommendation. */}
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="pilot-summary-recommendation">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            2. Recommandation de démonstration
          </h3>
          <dl>
            <Row
              label="Type d'action"
              value={`${ACTION_LABELS[rec.action_type] || rec.action_type} (${rec.action_type})`}
              testid="pilot-summary-rec-action"
            />
            <Row
              label="Audience"
              value={`${recAudience.source_market_name} (${recAudience.source_market_code})`}
              testid="pilot-summary-rec-audience"
            />
            <Row label="Période d'évidence" value={frPeriod(rec.evidence_period)} testid="pilot-summary-rec-period" />
            <Row label="Période de comparaison" value={frPeriod(rec.comparison_period)} testid="pilot-summary-rec-comparison" />
            <Row label="Période de suivi proposée" value={frPeriod(rec.proposed_monitoring_period)} testid="pilot-summary-rec-monitoring" />
          </dl>
          <p className="mt-2 text-xs italic text-slate-500" data-testid="pilot-summary-rec-note">
            Recommandation déterministe de démonstration
          </p>
        </div>

        {/* Step 3 - Simulated campaign. */}
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="pilot-summary-campaign">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            3. Campagne simulée
          </h3>
          <dl>
            <Row
              label="Audience"
              value={`${cmpAudience.source_market_name} (${cmpAudience.source_market_code})`}
              testid="pilot-summary-camp-audience"
            />
            <Row label="Période fictive" value={frPeriod(cmpPeriod.month)} testid="pilot-summary-camp-period" />
          </dl>
          <div className="mt-2 flex items-center gap-2 text-sm text-slate-800" data-testid="pilot-summary-camp-budget">
            <AssumptionTag />
            <span><span className="font-semibold">Budget fictif : </span>{fmtMoney(cmpBudget.amount, cmpBudget.currency)}</span>
          </div>
          <div className="mt-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Canaux synthétiques</p>
            <ul className="mt-1 space-y-1" data-testid="pilot-summary-camp-channels">
              {cmpChannels.map((ch, i) => (
                <li
                  key={`ch-${i}`}
                  className="flex items-center gap-2 text-sm text-slate-800"
                  data-testid={`pilot-summary-camp-channel-${i}`}
                >
                  <AssumptionTag />
                  <span>{ch.label}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Objectifs synthétiques</p>
            <ul className="mt-1 space-y-1" data-testid="pilot-summary-camp-objectives">
              {cmpObjectives.map((ob, i) => (
                <li
                  key={`ob-${i}`}
                  className="flex items-center gap-2 text-sm text-slate-800"
                  data-testid={`pilot-summary-camp-objective-${i}`}
                >
                  <AssumptionTag />
                  <span>{ob.label}</span>
                </li>
              ))}
            </ul>
          </div>
          {cmpAssumptions.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Hypothèses disponibles</p>
              <ul className="mt-1 space-y-1" data-testid="pilot-summary-camp-assumptions">
                {cmpAssumptions.map((a, i) => (
                  <li
                    key={`as-${i}`}
                    className="flex items-center gap-2 text-sm text-slate-800"
                    data-testid={`pilot-summary-camp-assumption-${i}`}
                  >
                    <AssumptionTag />
                    <span><span className="font-semibold">{a.kind} : </span>{a.detail}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <span
              className="inline-flex items-center rounded-full border border-slate-300 bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-700"
              data-testid="pilot-summary-camp-activation"
            >
              Activation désactivée
            </span>
            <span
              className="inline-flex items-center rounded-full border border-slate-300 bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-700"
              data-testid="pilot-summary-camp-publish"
            >
              Publication désactivée
            </span>
          </div>
        </div>

        {/* Step 4 - Simulated non-predictive outcomes. */}
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="pilot-summary-outcomes">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            4. Résultats simulés non prédictifs
          </h3>
          <dl className="grid grid-cols-2 gap-4">
            <div>
              <dt className="text-xs text-slate-500">Impressions — scénario</dt>
              <dd className="text-lg font-bold text-slate-900" data-testid="pilot-summary-outcome-impressions">
                {fmtInt(disp.scenario_impressions)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500">Couverture — scénario</dt>
              <dd className="text-lg font-bold text-slate-900" data-testid="pilot-summary-outcome-reach">
                {fmtInt(disp.scenario_reach)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500">Clics — scénario</dt>
              <dd className="text-lg font-bold text-slate-900" data-testid="pilot-summary-outcome-clicks">
                {fmtInt(disp.scenario_clicks)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500">Conversions — scénario</dt>
              <dd className="text-lg font-bold text-slate-900" data-testid="pilot-summary-outcome-conversions">
                {fmtInt(disp.scenario_conversions)}
              </dd>
            </div>
          </dl>
          <p className="mt-2 text-xs italic text-slate-500" data-testid="pilot-summary-outcomes-note">
            {OUTCOMES_NOTE}
          </p>
        </div>
      </div>

      {/* Transparency block (from summaryResult.transparency). */}
      <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4" data-testid="pilot-summary-transparency">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Transparence</h3>
        <ul className="space-y-1 text-sm text-slate-700">
          <li data-testid="pilot-summary-transparency-historical">Données historiques : synthétiques</li>
          <li data-testid="pilot-summary-transparency-campaign">Campagne : fictive</li>
          <li data-testid="pilot-summary-transparency-outcomes">Résultats : simulés</li>
          <li data-testid="pilot-summary-transparency-observed">Résultats non observés</li>
          <li data-testid="pilot-summary-transparency-predictive">Résultats non prédictifs</li>
        </ul>
        {/* Machine-readable transparency values, screen-reader only. */}
        <span className="sr-only" data-testid="pilot-summary-transparency-values">
          {`${t.historical_data}/${t.campaign}/${t.outcomes}`}
        </span>
      </div>

      {/* Consolidated disclaimer ONLY (single occurrence; source ones not shown). */}
      <p className="mt-4 text-xs text-slate-500" data-testid="pilot-summary-disclaimer">
        {s.disclaimers.summary}
      </p>
    </section>
  );
}
